/** A bank account's history, oldest first: what happened, who did it, when, and the system's evidence. */
import { Button, Loader, Modal } from '@insio/ui-toolkit';
import { PartyBankAccountEventType } from '@insio/shared-types';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFormatTimestamp } from '@/hooks/use-date-formatters';
import { useDelayedLoading } from '@/hooks/use-delayed-loading';
import { useBankAccountEvents, type PartyBankAccountEvent } from '../hooks/use-bank-account-events';
import type { PartyBankAccount } from '../hooks/use-party-bank-accounts';
import { parseSystemCheckEvidence } from '../utils/system-check-evidence';
import { BankAccountSummary } from './BankAccountSummary';
import { SystemCheckEvidenceDetails } from './SystemCheckEvidenceDetails';

interface BankAccountHistoryModalProps {
  account: PartyBankAccount;
  partyName: string | null;
  onClose: () => void;
}

export function BankAccountHistoryModal({
  account,
  partyName,
  onClose
}: BankAccountHistoryModalProps) {
  const t = useFeatureTranslation('partyBankAccounts.history');
  const { events, hasMore, loadMore, loading, loadingMore, error } = useBankAccountEvents(
    account.id
  );
  const showLoader = useDelayedLoading(loading);

  let content;
  if (loading) {
    content = showLoader ? (
      <div className='flex justify-center py-lg'>
        <Loader size='sm' />
      </div>
    ) : null;
  } else if (error && events.length === 0) {
    content = <p className='text-sm text-error-text'>{t('loadError')}</p>;
  } else if (events.length === 0) {
    content = <p className='text-sm text-foreground-muted'>{t('empty')}</p>;
  } else {
    content = (
      <ol className='flex flex-col gap-sm' data-testid='bank-account-history'>
        {events.map(event => (
          <HistoryEntry key={event.id} event={event} partyName={partyName} />
        ))}
      </ol>
    );
  }

  return (
    <Modal open onClose={onClose} size='md' testId='bank-account-history-modal'>
      <Modal.Header onClose={onClose} label={t('title')} />
      <Modal.Content>
        <div className='flex flex-col gap-md'>
          <BankAccountSummary account={account} />
          {content}
          {hasMore && (
            <Button
              variant='default'
              outline='default'
              size='sm'
              className='self-center'
              disabled={loadingMore}
              onClick={() => void loadMore()}
            >
              {t('loadMore')}
            </Button>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Action>
          <Button variant='default' onClick={onClose}>
            {t('close')}
          </Button>
        </Modal.Action>
      </Modal.Footer>
    </Modal>
  );
}

function HistoryEntry({
  event,
  partyName
}: {
  event: PartyBankAccountEvent;
  partyName: string | null;
}) {
  const t = useFeatureTranslation('partyBankAccounts.history');
  const tEvent = useEnumTranslation('partyBankAccountEventType');
  const formatTimestamp = useFormatTimestamp();
  const actor = event.isSystemActor ? t('system') : (event.actor?.displayName ?? t('unknownActor'));
  const evidence =
    event.eventType === PartyBankAccountEventType.SYSTEM_CHECKED
      ? parseSystemCheckEvidence(event.evidence)
      : null;

  return (
    <li
      className='flex flex-col gap-xs rounded-lg border border-border-base px-md py-sm'
      data-testid={`bank-account-event-${event.eventType}`}
    >
      <p className='text-sm font-medium text-foreground-base'>{tEvent(event.eventType)}</p>
      <p className='text-xs text-foreground-muted'>
        {actor} · {formatTimestamp(event.occurredAt)}
      </p>
      {event.reason && (
        <p className='text-sm text-foreground-base'>{t('reason', { reason: event.reason })}</p>
      )}
      {evidence && <SystemCheckEvidenceDetails evidence={evidence} partyName={partyName} />}
    </li>
  );
}
